(ns io.daskul.webapp.widgets.form.lookup
  "Lookup field: selector with HTTP-fetched or static options.

   Handles:
   - HTTP lookups (fetch options from server)
   - Dependent fields (other fields filter this one's options — :depends-on
     takes one {:field :filter-param} map or a vector of them, #792)
   - Autocomplete with debounced search
   - Static options from schema/ui hints
   - Auto-fill: populate other form fields from selected record

   Delegates rendering to selector-field."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [re-frame.core :as rf]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.widgets.display :as display]
   [io.daskul.webapp.widgets.form.lookup-logic :as lookup-logic]
   [io.daskul.webapp.widgets.form.selector :refer [selector-field]]
   [io.daskul.webapp.widgets.form.lookup-create :refer [lookup-inline-create]]
   [io.daskul.webapp.widgets.layout.slide-over :refer [slide-over]]))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- unwrap-maybe
  "Unwrap [\"maybe\" X] -> X, pass through anything else."
  [schema]
  (if (and (vector? schema) (= "maybe" (first schema)))
    (second schema)
    schema))

(defn- extract-static-options
  "Extract static options from ui config or schema.
   Handles [:maybe [:enum ...]] wrapping.
   Uses :options map from ui for explicit labels, falls back to humanize.
   Returns [{:value \"...\" :label \"...\"}] or nil."
  [schema ui]
  (let [effective (unwrap-maybe schema)
        options-map (:options ui)
        make-option (fn [v]
                      {:value v
                       :label (or (get options-map v)
                                  (display/humanize-enum-value v)
                                  v)})]
    (or
     ;; From inline UI :options [{:value "..." :label "..."}]
     (when (and (vector? options-map)
                (seq options-map)
                (map? (first options-map))
                (:value (first options-map)))
       options-map)
     ;; From [:vector [:enum ...]] schema
     (when (and (vector? effective) (= "vector" (first effective)))
       (let [inner (second effective)]
         (when (and (vector? inner) (= "enum" (first inner)))
           (mapv make-option (rest inner)))))
     ;; From [:enum ...] schema (or [:maybe [:enum ...]] after unwrap)
     (when (and (vector? effective) (= "enum" (first effective)))
       (mapv make-option (rest effective))))))

;; ============================================================================
;; Auto-fill: fetch related record and map to form field values
;; ============================================================================

(defn fetch-auto-fill!
  "Fetch the related record via GET and map fields per auto-fill config.
   Derives GET path from lookup URL: /things/lookup → /things/:id.
   Each field config has :path (extract from record) or :value (static).
   Calls on-auto-fill with {:values {field val} :readonly-keys #{field}}.
   Routes through api/request! (shared base URL + auth + 401 policy)."
  [lookup-url selected-id auto-fill on-auto-fill]
  (let [base-path (str/replace lookup-url #"/lookup$" "")]
    (api/request!
     {:method :get
      :path (str base-path "/" selected-id)
      :on-success
      (fn [record]
        (let [values (into {}
                       (map (fn [[target cfg]]
                              (let [k (keyword target)]
                                [k (if-let [path (:path cfg)]
                                     (get-in record (mapv keyword path))
                                     (:value cfg))])))
                       (:fields auto-fill))
              readonly-keys (into #{}
                              (keep (fn [[target cfg]]
                                      (when (:readonly cfg) (keyword target))))
                              (:fields auto-fill))]
          (on-auto-fill {:values values :readonly-keys readonly-keys})))
      :on-error
      (fn [err]
        (js/console.error "Auto-fill fetch failed:" err))})))

(defn- create-label-of
  "What '+ Add …' should say, and what to title the create slide-over.

   The server's `:create :label` names the THING (from the target aggregate),
   which is not always what the FIELD is called. For a relationship FK the two
   coincide — a Guardian field pointing at a Guardian — so the old fallback
   chain read fine. A transition param is named for its ROLE, and a \"Bill to\"
   field offered '+ Add Bill to...' (#803).

   Falls back to the old chain for anything the server has not labelled."
  [create-config ui field-key]
  (or (:label create-config) (:label ui) (:relationship ui) (name field-key)))

;; ============================================================================
;; Lookup Field
;; ============================================================================

(defui lookup-field
  "Selector field with HTTP lookup support.

   Props:
   - agg-key: keyword - aggregate key for re-frame state
   - field: map - field definition with :name, :schema, :ui
   - value: any - current field value
   - on-change: fn - called with new value
   - disabled: boolean
   - form-data: map - full form state (for dependent field values)
   - required: boolean - show required asterisk
   - error: string | nil - external error (overrides subscription)
   - form-field-component: component - for rendering inline create form fields
   - on-auto-fill: fn | nil - called with {:values {} :readonly-keys #{}} or nil (clear)"
  [{:keys [agg-key field value on-change disabled form-data
           required error form-field-component on-auto-fill]}]
  (let [field-key (:name field)
        schema (:schema field)
        ui (:ui field)
        base-lookup-url (:lookup-url ui)
        widget (keyword (or (:widget ui) "select"))

        ;; Inline create support
        create-config (:create ui)
        [creating? set-creating!] (uix/use-state false)

        ;; Static options from schema or ui hints
        static-options (extract-static-options schema ui)
        has-lookup-url? (some? base-lookup-url)

        ;; Dependent field config — single map or vector of maps (#792)
        depends-on (lookup-logic/normalize-depends-on (:depends-on ui))
        {dependent-params :params
         missing-dependents :missing
         dependent-signature :signature} (lookup-logic/dependent-state depends-on form-data)

        ;; Track previous dependent values to detect changes
        prev-dependent-ref (uix/use-ref dependent-signature)

        ;; Autocomplete search state
        [search-query set-search-query!] (uix/use-state "")
        search-timeout-ref (uix/use-ref nil)
        min-chars (or (:min-chars ui) 2)

        ;; value-field: request a different field as :value in response
        value-field (:value-field ui)

        ;; Default filters from schema config (per-relationship or as-reference)
        default-filters (:default-filters ui)

        ;; Every request this widget makes goes through build-lookup-url, so the
        ;; search path can't drop a filter the initial load applied (#799)
        url-params {:default-filters default-filters
                    :dependent-params dependent-params
                    :value-field value-field}

        lookup-url (lookup-logic/build-lookup-url
                    base-lookup-url
                    (assoc url-params
                           :query (when (and (= widget :autocomplete)
                                             (>= (count search-query) min-chars))
                                    search-query)))

        ;; Disable while any dependent field has no value yet
        waiting-for-dependent? (and has-lookup-url? (boolean (seq missing-dependents)))

        ;; Don't fetch until dependent field has a value and autocomplete has enough chars
        should-fetch? (and has-lookup-url?
                           (not waiting-for-dependent?)
                           (if (= widget :autocomplete)
                             (>= (count search-query) min-chars)
                             true))

        ;; Options from re-frame state or static
        fetched-options (urf/use-subscribe [::agg.subs/lookup-options agg-key field-key])
        fetched-loading? (urf/use-subscribe [::agg.subs/lookup-loading? agg-key field-key])
        options (if has-lookup-url? fetched-options static-options)
        loading? (and has-lookup-url? fetched-loading?)
        ;; Prefer external error (from child inline form) over subscription
        default-error (urf/use-subscribe [::agg.subs/field-error agg-key field-key])
        field-error (if (some? error) error default-error)

        ;; Ensure label fallback for selector-field (humanize field name)
        effective-ui (cond-> ui
                       (not (:label ui))
                       (assoc :label (->> (str/split (name field-key) #"-")
                                          (map str/capitalize)
                                          (str/join " "))))

        ;; Clear field error + call on-change
        handle-change (fn [v]
                        (when field-error
                          (rf/dispatch [::agg.events/clear-field-error agg-key field-key]))
                        (on-change v))

        ;; For autocomplete: debounced search handler
        handle-search-change (fn [query]
                               (set-search-query! query)
                               (when-let [timeout @search-timeout-ref]
                                 (js/clearTimeout timeout))
                               (when (>= (count query) min-chars)
                                 (let [search-url (lookup-logic/build-lookup-url
                                                   base-lookup-url
                                                   (assoc url-params :query query))]
                                   (reset! search-timeout-ref
                                           (js/setTimeout
                                            #(rf/dispatch [::agg.events/lookup agg-key search-url field-key])
                                            300)))))]

    ;; Clear selection and options when any dependent field value changes
    (uix/use-effect
     (fn []
       (when has-lookup-url?
         (let [prev-sig @prev-dependent-ref]
           (when (and (some? prev-sig) (not= prev-sig dependent-signature))
             (rf/dispatch-sync [::agg.events/clear-lookup-options agg-key field-key])
             (on-change nil))
           (reset! prev-dependent-ref dependent-signature)))
       js/undefined)
     [agg-key field-key on-change dependent-signature has-lookup-url?])

    ;; Fetch lookup options on mount and when lookup-url changes
    (uix/use-effect
     (fn []
       (when (and has-lookup-url? lookup-url should-fetch? (not= widget :autocomplete))
         (rf/dispatch [::agg.events/lookup agg-key lookup-url field-key]))
       js/undefined)
     [agg-key field-key lookup-url should-fetch? widget has-lookup-url?])

    ;; Autocomplete: resolve label for existing value on mount (edit page).
    ;; Without this, a disabled autocomplete shows placeholder instead of the name.
    (uix/use-effect
     (fn []
       (when (and has-lookup-url? base-lookup-url (= widget :autocomplete)
                  value (empty? options))
         (rf/dispatch [::agg.events/lookup agg-key
                       (str base-lookup-url "?resolve-id=" value)
                       field-key]))
       js/undefined)
     [agg-key field-key base-lookup-url widget value options has-lookup-url?])

    ;; Auto-select when there's exactly one option and no value yet.
    ;; Skip for autocomplete — user explicitly searches and selects.
    (uix/use-effect
     (fn []
       (when (and (not= widget :autocomplete)
                  (= 1 (count options))
                  (nil? value)
                  (not loading?))
         (handle-change (:value (first options))))
       js/undefined)
     [handle-change options value loading? widget])

    ($ :<>
      ;; Selector (always visible, never disabled by create)
      ($ selector-field
        {:field-key field-key
         :value value
         :on-change handle-change
         :options options
         :loading? loading?
         :disabled? (or disabled
                       (and loading? (not= widget :autocomplete))
                       waiting-for-dependent?)
         :error field-error
         :required required
         :ui effective-ui
         :waiting-for-dependent? waiting-for-dependent?
         :dependent-field-name (when (seq missing-dependents)
                                 (lookup-logic/missing-label missing-dependents))
         :on-search handle-search-change
         :search-query search-query
         :on-create-new (when (and create-config (not disabled))
                          #(set-creating! true))
         :create-label (when create-config
                         (create-label-of create-config ui field-key))})
      ;; Slide-over for inline create
      (when create-config
        (let [create-label (create-label-of create-config ui field-key)]
          ($ slide-over
            {:open? creating?
             :on-close #(set-creating! false)
             :title (str "New " create-label)
             :size "lg"}
            (when creating?
              ($ lookup-inline-create
                {:resource (:resource create-config)
                 :label create-label
                 :on-created (fn [new-id]
                               (set-creating! false)
                               ;; Resolve the new entity's label via lookup endpoint
                               (when has-lookup-url?
                                 (rf/dispatch [::agg.events/lookup agg-key
                                              (str base-lookup-url "?resolve-id=" new-id)
                                              field-key]))
                               ;; Auto-select the newly created entity
                               (handle-change (str new-id)))
                 :on-cancel #(set-creating! false)
                 :form-field-component form-field-component}))))))))
